import React, { useEffect, useState } from 'react';
import { ArrowUpRight, Check, FileText, RefreshCw } from 'lucide-react';
import WorkspacePaneToggle from './WorkspacePaneToggle';
import { architectApi, estimateApi, EstimateFull, EstimateDays } from './api';
import { fmtDate } from './utils';

const daysLabel = (value: EstimateDays) => Array.isArray(value) ? `${value[0]}–${value[1]}` : String(value);

/** Read-only reference to the approved estimate; pricing retains its own API and arithmetic. */
export default function EstimateReferencePane({ projectId, active, revision, onOpenEstimate, open = true, onToggle, pricingSource = false, canOpenEstimate = true }: {
  projectId: string; active: boolean; revision: number; onOpenEstimate: () => void;
  open?: boolean; onToggle?: () => void; pricingSource?: boolean; canOpenEstimate?: boolean;
}) {
  const [document, setDocument] = useState<Pick<EstimateFull, 'id' | 'title' | 'status' | 'spec' | 'created_at' | 'reviewed_at' | 'reviewed_by_name'> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true); setError('');
    (async () => {
      if (pricingSource) {
        const result = await architectApi.pricingEstimateReference(projectId);
        if (!cancelled) setDocument(result.estimate);
        return;
      }
      const list = await estimateApi.listEstimates(projectId);
      // Match the source selected by pricing/proposal: the most recently
      // approved estimate can be an older document that was re-approved.
      const approved = list.estimates.filter((row) => row.status === 'approved').sort((a, b) =>
        (b.reviewed_at || '').localeCompare(a.reviewed_at || '') || b.created_at.localeCompare(a.created_at),
      )[0];
      const result = approved ? (await estimateApi.getEstimate(approved.id)).estimate : null;
      if (!cancelled) setDocument(result);
    })().catch(() => { if (!cancelled) setError('The estimate could not be loaded.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, active, revision, retry, pricingSource]);
  const spec = document?.spec;
  const cases = spec?.cases || (spec?.modules ? [{ name: spec.system, modules: spec.modules }] : []);
  return (
    <aside id="pricing-estimate-reference" className="arch-estimate-reference" aria-label="Approved estimate reference" hidden={!open}>
      <header className="arch-reference-header arch-workspace-pane-head">
        <FileText className="arch-reference-heading-icon" size={16} aria-hidden="true" />
        <h2>Approved estimate</h2>
        {onToggle && <WorkspacePaneToggle open label="estimate" shortLabel controls="pricing-estimate-reference" onToggle={onToggle} />}
      </header>
      <div className="arch-reference-scroll">
        {loading ? <div className="arch-reference-skeleton" role="status" aria-label="Loading estimate"><i /><i /><i /><i /></div> : error ? (
          <div className="arch-reference-empty"><p>{error}</p><button className="arch-btn arch-btn-outline" onClick={() => setRetry((value) => value + 1)}><RefreshCw size={14} /> Retry</button></div>
        ) : !document ? <div className="arch-reference-empty"><FileText size={28} /><h3>No approved estimate yet</h3><p>Review and approve an estimate to see its scope here.</p>{canOpenEstimate ? <button className="arch-btn arch-btn-outline" onClick={onOpenEstimate}>Open estimate <ArrowUpRight size={14} /></button> : null}</div> : <>
          <div className="arch-reference-intro"><span className="arch-reference-approved"><Check size={12} /> Approved</span><h3>{spec?.system || document.title}</h3><p>{document.reviewed_by_name ? `Approved by ${document.reviewed_by_name}` : 'Approved estimate'}{document.reviewed_at ? ` · ${fmtDate(document.reviewed_at)}` : ''}</p></div>
          {cases.map((item, index) => <article className="arch-reference-case" key={index}>
            <div className="arch-reference-case-heading"><span>{String(index + 1).padStart(2, '0')}</span><h3>{item.name}</h3></div>
            {item.modules.map((module, position) => <div className="arch-reference-module" key={position}><div><strong>{module.name}</strong><p>{module.description}</p>{module.apis?.length > 0 && <small>{module.apis.join(' · ')}</small>}{module.tbd_reason && <small>{module.tbd_reason}</small>}</div><span className="arch-reference-days">{daysLabel(module.days)}<small>days</small></span></div>)}
          </article>)}
          {spec?.total_label && <div className="arch-reference-total"><span>Estimated effort</span><strong>{spec.total_label}</strong></div>}
          {(spec?.notes || spec?.important)?.length ? <div className="arch-reference-notes"><h3>Notes & assumptions</h3><ul>{(spec?.notes || spec?.important || []).map((note, index) => <li key={index}>{note}</li>)}</ul></div> : null}
          {spec?.summary && <div className="arch-reference-notes"><h3>Delivery</h3><p>{spec.summary.deliverables}</p><p>{spec.summary.team}</p></div>}
        </>}
      </div>
    </aside>
  );
}
